import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import { AcceptedInviteIcon, InvitationExpired } from "../../assets";
import AuthPageLayout from "../../components/Authentication/AuthPageLayout";
import authService from "../../services/AuthService";
import logger from "../../services/Logger";
import { persistAuthSession } from "../../services/AuthSession";
import { loginWithFirebase } from "../../api/auth";
import { getModeAuth } from "../../utils/sparkApi/getModeAuth";
import UserAuthStore from "../../store/userStore";

// ─── Success state ────────────────────────────────────────────────────────────

const PasswordResetSuccess = () => {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(10);

  useEffect(() => {
    const countdown = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    if (secondsLeft <= 0) {
      clearInterval(countdown);
      authService.signOut().finally(() => navigate("/"));
    }

    return () => clearInterval(countdown);
  }, [secondsLeft, navigate]);

  return (
    <AuthPageLayout>
      <div
        className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card"
        style={{ boxShadow: "0px 1px 4px 0px #00000035" }}
      >
        <div className="flex justify-center items-center">
          <AcceptedInviteIcon className="fill-ink-hex-00be07 w-16 sm:w-auto" />
          </div>

          <div className="text-center mt-[20px]">
            <h2 className="text-[13px] sm:text-[14px] font-semibold dark:text-ink-white text-ink-ink">
              Password Updated!
            </h2>
          </div>

          <div className="text-center mt-[20px]">
            <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white">
              Your password has been set successfully. You can now sign in with
              your new password.
            </p>
            <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white mt-[10px]">
              If you are not redirected automatically,{" "}
              <button
                className="underline font-semibold"
                onClick={() => authService.signOut().finally(() => navigate("/"))}
              >
                click here
              </button>
              .
            </p>
          </div>

          <div className="mt-3 sm:mt-[15px] text-[9px] sm:text-[10px] text-ink-muted-control dark:text-ink-muted-text-dark text-center">
            Redirecting to sign-in in {secondsLeft} second{secondsLeft !== 1 && "s"}…
          </div>
      </div>
    </AuthPageLayout>
  );
};

// ─── Error state ──────────────────────────────────────────────────────────────

const PasswordResetError = ({ message }) => {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(15);

  useEffect(() => {
    const countdown = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    if (secondsLeft <= 0) {
      clearInterval(countdown);
      navigate("/");
    }

    return () => clearInterval(countdown);
  }, [secondsLeft, navigate]);

  return (
    <AuthPageLayout>
      <div
        className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card"
        style={{ boxShadow: "0px 1px 4px 0px #00000035" }}
      >
        <div className="flex justify-center items-center">
          <InvitationExpired className="fill-ink-hex-ff1514 w-16 sm:w-auto" />
          </div>

          <div className="text-center mt-[20px]">
            <h2 className="text-ink-hex-ff1514 text-[13px] sm:text-[14px] font-semibold">
              Link Expired or Invalid
            </h2>
          </div>

          <div className="text-center mt-[20px]">
            <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white">
              {message || "This password reset link has expired or already been used. Please request a new one from the sign-in page."}
            </p>
            <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white mt-[10px]">
              If you are not redirected automatically,{" "}
              <a className="underline font-semibold" href="/">
                click here
              </a>
              .
            </p>
          </div>

          <div className="mt-3 sm:mt-[15px] text-[9px] sm:text-[10px] text-ink-muted-control dark:text-ink-muted-text-dark text-center">
            Page will redirect in {secondsLeft} second{secondsLeft !== 1 && "s"}…
          </div>
      </div>
    </AuthPageLayout>
  );
};

// ─── Sign-in link handler ────────────────────────────────────────────────────

const SignInLinkHandler = () => {
  const navigate = useNavigate();
  const setAuthenticated = UserAuthStore((state) => state.setAuthenticated);
  const [status, setStatus] = useState("loading"); // loading | awaiting-email | signing-in | success | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [emailPrompt, setEmailPrompt] = useState("");
  const [emailPromptError, setEmailPromptError] = useState(false);

  const completeSignIn = async (email) => {
    try {
      await authService.signOut().catch(() => {});
      const url = window.location.href;
      const firebaseUser = await authService.signInWithEmailLink(email, url);
      localStorage.removeItem("emailForSignIn");

      // Apply pending display name if this is a first sign-in via magic link
      const pendingName = localStorage.getItem("nameForSignIn");
      if (pendingName && !firebaseUser.displayName) {
        await authService.updateProfile(firebaseUser, { displayName: pendingName });
        localStorage.removeItem("nameForSignIn");
      }

      // Exchange Firebase token for backend session
      const firebaseToken = await authService.getIdToken(firebaseUser, true);
      const { session_token, user_id, org_id } = await loginWithFirebase(firebaseToken);

      persistAuthSession({ sessionToken: session_token, userId: user_id, orgId: org_id });
      setAuthenticated(firebaseUser, org_id);
      await getModeAuth();

      setStatus("success");
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      logger.error("SignInLinkHandler", "Sign-in with email link failed", { error: err.message });
      setErrorMsg(err.message || "Sign-in failed. The link may have expired or already been used.");
      setStatus("error");
    }
  };

  useEffect(() => {
    const email = localStorage.getItem("emailForSignIn");
    if (!email) {
      setStatus("awaiting-email");
    } else {
      setStatus("signing-in");
      completeSignIn(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = async () => {
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailPrompt.trim());
    if (!valid) { setEmailPromptError(true); return; }
    setEmailPromptError(false);
    setStatus("signing-in");
    await completeSignIn(emailPrompt.trim());
  };

  if (status === "error") return <PasswordResetError message={errorMsg} />;

  if (status === "awaiting-email") {
    return (
      <AuthPageLayout>
        <div className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card" style={{ boxShadow: "0px 1px 4px 0px #00000035" }}>
          <div className="text-center mb-4">
            <h2 className="text-[14px] font-semibold dark:text-ink-white text-ink-ink">Confirm your email</h2>
            <p className="text-[12px] text-ink-muted-control dark:text-ink-muted-text-dark mt-2">
              To finish signing in, please enter the email address you used to request this link.
            </p>
          </div>
          <input
            type="email"
            placeholder="Enter your email"
            value={emailPrompt}
            onChange={(e) => { setEmailPrompt(e.target.value); setEmailPromptError(false); }}
            onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
            className={`photon-field w-full px-4 py-2 text-sm rounded-lg border ${
              emailPromptError ? "photon-field-invalid border-edge-red-500" : ""
            }`}
          />
          {emailPromptError && (
            <p className="text-[11px] text-ink-red-500 mt-1">Please enter a valid email address.</p>
          )}
          <button
            onClick={handleEmailSubmit}
            className="photon-primary-action mt-3 w-full px-4 py-2 text-sm font-medium rounded-full bg-button-primary text-button-on-primary transition-colors"
          >
            Continue
          </button>
        </div>
      </AuthPageLayout>
    );
  }

  if (status === "success") {
    return (
      <AuthPageLayout>
        <div className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card" style={{ boxShadow: "0px 1px 4px 0px #00000035" }}>
          <div className="flex justify-center items-center">
            <AcceptedInviteIcon className="fill-ink-hex-00be07 w-16 sm:w-auto" />
          </div>
          <div className="text-center mt-[20px]">
            <h2 className="text-[13px] sm:text-[14px] font-semibold dark:text-ink-white text-ink-ink">Signed In!</h2>
          </div>
          <div className="text-center mt-[20px]">
            <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white">
              You have been signed in successfully.
            </p>
          </div>
          <div className="mt-3 sm:mt-[15px] text-[9px] sm:text-[10px] text-ink-muted-control dark:text-ink-muted-text-dark text-center">
            Redirecting you now…
          </div>
        </div>
      </AuthPageLayout>
    );
  }

  // Loading / signing-in state
  return (
    <div className="min-h-screen flex items-center justify-center dark:bg-surface-page bg-surface-page">
      <p className="text-[13px] text-ink-muted-control dark:text-ink-muted-text-dark">Signing you in…</p>
    </div>
  );
};

// ─── Main form ────────────────────────────────────────────────────────────────

const ActionPage = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successState, setSuccessState] = useState(false);
  const [errorState, setErrorState] = useState(null);

  // Validate link presence on mount
  useEffect(() => {
    if (mode === "signIn") return; // handled by SignInLinkHandler
    if (!oobCode || mode !== "resetPassword") {
      setErrorState("Invalid or missing reset link. Please request a new password reset from the sign-in page.");
    }
  }, [oobCode, mode]);

  if (mode === "signIn") return <SignInLinkHandler />;
  if (successState) return <PasswordResetSuccess />;
  if (errorState) return <PasswordResetError message={errorState} />;

  const passwordValid = password.length >= 6;
  const confirmValid = password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!passwordValid || !confirmValid) return;

    setLoading(true);
    try {
      await authService.confirmPasswordReset(oobCode, password);
      setSuccessState(true);
    } catch (error) {
      logger.error("ActionPage", "Password reset failed", { error: error.message });
      setErrorState(error.message || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <div
        className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card"
        style={{ boxShadow: "0px 1px 4px 0px #00000035" }}
      >
        <div className="flex justify-center items-center mb-[16px]">
          <div className="w-[56px] h-[56px] rounded-full border-2 border-edge-primary flex items-center justify-center">
              <Lock className="photon-text-link w-6 h-6" />
            </div>
          </div>

          <h2 className="text-[13px] sm:text-[14px] font-semibold text-center dark:text-ink-white text-ink-ink mb-[6px]">
            Set New Password
          </h2>
          <p className="text-[11px] sm:text-[12px] text-center text-ink-muted-control dark:text-ink-muted-text-dark mb-[20px]">
            Choose a new password for your account.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            {/* New password */}
            <div className="mb-[14px]">
              <label className="block text-[11px] sm:text-[12px] text-ink-ink dark:text-ink-muted-text-dark mb-[6px]">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="photon-field w-full border rounded-lg px-3 py-2.5 pr-[38px]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-[10px] top-1/2 -translate-y-1/2 text-ink-muted-control dark:text-ink-muted-text-dark"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {submitted && !passwordValid && (
                <p className="text-[10px] text-ink-hex-ff1514 mt-[4px]">
                  Password must be at least 6 characters.
                </p>
              )}
            </div>

            {/* Confirm password */}
            <div className="mb-[20px]">
              <label className="block text-[11px] sm:text-[12px] text-ink-ink dark:text-ink-muted-text-dark mb-[6px]">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  className="photon-field w-full border rounded-lg px-3 py-2.5 pr-[38px]"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-[10px] top-1/2 -translate-y-1/2 text-ink-muted-control dark:text-ink-muted-text-dark"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {submitted && passwordValid && !confirmValid && (
                <p className="text-[10px] text-ink-hex-ff1514 mt-[4px]">
                  Passwords do not match.
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="photon-primary-action w-full py-[10px] rounded-[8px] text-[12px] sm:text-[13px] font-semibold
                bg-button-primary text-button-on-primary
                disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Updating…" : "Set Password"}
            </button>
          </form>
      </div>
    </AuthPageLayout>
  );
};

export default ActionPage;
